import React, { FC, useState } from 'react';
import { useGetProgramMasterData } from 'src/api/mutations/useGetProgramMasterData';
import { isFemaleProfile } from 'src/utils/genderUtils';
import './IncreaseStrengthSteps.scss';

import BuildstrengthMaleFullBodyStrengthIcon from 'url:src/assets/images/trainer/program-generator/buildstrength-male-full-body-strength.webp';
import BuildstrengthMaleUpperBodyStrengthIcon from 'url:src/assets/images/trainer/program-generator/buildstrength-male-upper-body-strength.webp';
import BuildstrengthMaleLowerBodyStrengthIcon from 'url:src/assets/images/trainer/program-generator/buildstrength-male-lower-body-strength.webp';
import BuildstrengthMaleCoreStrengthIcon from 'url:src/assets/images/trainer/program-generator/buildstrength-male-core-strength.webp';
import BuildstrengthMaleSpecificLiftMovementIcon from 'url:src/assets/images/trainer/program-generator/buildstrength-male-specific-lift-movement.webp';
import BuildstrengthFemaleFullBodyStrengthIcon from 'url:src/assets/images/trainer/program-generator/buildstrength-female-full-body-strength.webp';
import BuildstrengthFemaleUpperBodyStrengthIcon from 'url:src/assets/images/trainer/program-generator/buildstrength-female-upper-body-strength.webp';
import BuildstrengthFemaleLowerBodyStrengthIcon from 'url:src/assets/images/trainer/program-generator/buildstrength-female-lower-body-strength.webp';
import BuildstrengthFemaleCoreStrengthIcon from 'url:src/assets/images/trainer/program-generator/buildstrength-female-core-strength.webp';
import BuildstrengthFemaleSpecificLiftMovementIcon from 'url:src/assets/images/trainer/program-generator/buildstrength-female-specific-lift-movement.webp';

interface IncreaseStrengthStep1Props {
  value?: string[];
  clientBodyProfile?: string;
  onChange?: (value: string[]) => void;
}

// What should this program build strength for?
export const IncreaseStrengthStep1: FC<IncreaseStrengthStep1Props> = ({
  value = [],
  clientBodyProfile = 'Male',
  onChange,
}) => {
  const isFemale = isFemaleProfile(clientBodyProfile);
  const iconMap: Record<string, React.ReactNode> = {
    full_body_strength: isFemale ? (
      <img
        src={BuildstrengthFemaleFullBodyStrengthIcon}
        alt="BuildstrengthFemaleFullBodyStrengthIcon"
        className="program-svg-icon"
      />
    ) : (
      <img
        src={BuildstrengthMaleFullBodyStrengthIcon}
        alt="BuildstrengthMaleFullBodyStrengthIcon"
        className="program-svg-icon"
      />
    ),
    upper_body_strength: isFemale ? (
      <img
        src={BuildstrengthFemaleUpperBodyStrengthIcon}
        alt="BuildstrengthFemaleUpperBodyStrengthIcon"
        className="program-svg-icon"
      />
    ) : (
      <img
        src={BuildstrengthMaleUpperBodyStrengthIcon}
        alt="BuildstrengthMaleUpperBodyStrengthIcon"
        className="program-svg-icon"
      />
    ),
    lower_body_strength: isFemale ? (
      <img
        src={BuildstrengthFemaleLowerBodyStrengthIcon}
        alt="BuildstrengthFemaleLowerBodyStrengthIcon"
        className="program-svg-icon"
      />
    ) : (
      <img
        src={BuildstrengthMaleLowerBodyStrengthIcon}
        alt="BuildstrengthMaleLowerBodyStrengthIcon"
        className="program-svg-icon"
      />
    ),
    core_strength: isFemale ? (
      <img
        src={BuildstrengthFemaleCoreStrengthIcon}
        alt="BuildstrengthFemaleCoreStrengthIcon"
        className="program-svg-icon"
      />
    ) : (
      <img
        src={BuildstrengthMaleCoreStrengthIcon}
        alt="BuildstrengthMaleCoreStrengthIcon"
        className="program-svg-icon"
      />
    ),
    specific_lift_movement: isFemale ? (
      <img
        src={BuildstrengthFemaleSpecificLiftMovementIcon}
        alt="BuildstrengthFemaleSpecificLiftMovementIcon"
        className="program-svg-icon"
      />
    ) : (
      <img
        src={BuildstrengthMaleSpecificLiftMovementIcon}
        alt="BuildstrengthMaleSpecificLiftMovementIcon"
        className="program-svg-icon"
      />
    ),
  };

  const [selectedGoal, setSelectedGoal] = useState<string[]>(value);
  const { data: programMasterData, isLoading, isError } = useGetProgramMasterData();

  const stepData = programMasterData?.trainingGoalDetails?.groups?.find(
    (item: any) => item.goalId === 'increase_strength',
  );

  const title = stepData?.title;
  const options = stepData?.options
    ? [...stepData.options].sort((a: any, b: any) => (a.order || 0) - (b.order || 0))
    : [];

  if (isLoading) {
    return <div className="increase-strength-step">{'Loading options...'}</div>;
  }

  if (isError || !stepData) {
    return <div className="increase-strength-step">{'Error loading options.'}</div>;
  }

  return (
    <div className="increase-strength-step">
      <h2 className="step-title">{title}</h2>

      <div className="goal-cards-grid">
        {options.map((option: any) => {
          const icon = iconMap[option.id] || iconMap[option.image] || null;

          return (
            <div
              key={option.id}
              className={`goal-card ${selectedGoal.includes(option.slug) ? 'selected' : ''}`}
              onClick={() => {
                let nextValue = [...selectedGoal];
                if (nextValue.includes(option.slug)) {
                  nextValue = nextValue.filter((p) => p !== option.slug);
                } else {
                  nextValue.push(option.slug);
                }
                setSelectedGoal(nextValue);
                if (onChange) onChange(nextValue);
              }}
            >
              <div className="goal-image">{icon}</div>
              <span className="goal-label">{option.title}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
